import React, { useCallback, useContext, useEffect, useState } from 'react'
import AuthContext from '../context/AuthContext'
import {
    FormControl, Box, Paper, InputLabel, Button, TextField,
    Typography, Select, MenuItem, Snackbar, Divider,
    FormControlLabel, Checkbox, Grid, Chip, CircularProgress, Alert,
    Input, FormHelperText, Dialog, DialogTitle, DialogContent, DialogActions,
    Link as MuiLink
} from '@mui/material'
import { elUpperCase } from '../utils/text'
import MuiAlert from '@mui/material/Alert'
import PersonIcon from '@mui/icons-material/Person'
import SchoolIcon from '@mui/icons-material/School'
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus'
import HomeIcon from '@mui/icons-material/Home'
import PeopleIcon from '@mui/icons-material/People'
import AddIcon from '@mui/icons-material/Add'
import StudentAddressesComponent from './StudentAddressesComponent'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet'
import 'leaflet/dist/leaflet.css'
import L from 'leaflet'
import iconUrl from 'leaflet/dist/images/marker-icon.png'
import iconShadowUrl from 'leaflet/dist/images/marker-shadow.png'
import GoogleAddressSearch from './GoogleAddressSearch'

const DefaultIcon = L.icon({
    iconUrl,
    shadowUrl: iconShadowUrl,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
})
L.Marker.prototype.options.icon = DefaultIcon

function MapClickHandler({ onMapClick }) {
    useMapEvents({
        click(e) {
            onMapClick(e.latlng.lat, e.latlng.lng)
        },
    })
    return null
}

function FlyTo({ position }) {
    const map = useMap()
    useEffect(() => {
        if (position) map.flyTo(position, 16)
    }, [position, map])
    return null
}

const SectionHeader = ({ icon, title }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, mt: 3 }}>
        {icon}
        <Typography variant="subtitle1" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>
            {title}
        </Typography>
        <Divider sx={{ flex: 1, ml: 1 }} />
    </Box>
)

const StudentDetailsComponent = (props) => {
    const { authTokens, logoutUser, backendUrl, profile } = useContext(AuthContext)
    const { t } = useTranslation()
    const [studentName, setStudentName] = useState('')
    const [studentSurname, setStudentSurname] = useState('')
    const [studentId, setStudentId] = useState('')
    const [schoolClasses, setSchoolclasses] = useState([])
    const [schoolClassId, setSchoolClassId] = useState('')
    const [departments, setDepartments] = useState([])
    const [departmentId, setDepartmentId] = useState('')
    const [parents, setParents] = useState([])
    const [busToSchool, setBusToSchool] = useState(false)
    const [busFromSchool, setBusFromSchool] = useState(false)
    const [dobDay, setDobDay] = useState('')
    const [dobMonth, setDobMonth] = useState('')
    const [dobYear, setDobYear] = useState('')
    const [comments, setComments] = useState('')
    const [saved, setSaved] = useState(false)
    const [saving, setSaving] = useState(false)
    const [errors, setErrors] = useState({})
    const [saveError, setSaveError] = useState('')
    const [parentModalOpen, setParentModalOpen] = useState(false)
    const [selectedParent, setSelectedParent] = useState(null)
    const [createParentModalOpen, setCreateParentModalOpen] = useState(false)
    const [createParentForm, setCreateParentForm] = useState({ first_name: '', last_name: '', email: '', phone_number: '', notes: '' })
    const [createParentErrors, setCreateParentErrors] = useState({})
    const [createParentSaving, setCreateParentSaving] = useState(false)
    const [createParentError, setCreateParentError] = useState('')

    // Inline address form state
    const [showAddressForm, setShowAddressForm] = useState(false)
    const [newAddress, setNewAddress] = useState('')
    const [newLat, setNewLat] = useState(null)
    const [newLng, setNewLng] = useState(null)
    const [newComments, setNewComments] = useState('')
    const [savingAddress, setSavingAddress] = useState(false)
    const [addressRefreshKey, setAddressRefreshKey] = useState(0)

    const navigate = useNavigate()
    const isEditing = Boolean(studentId)

    useEffect(() => {
        getSchoolClasses()
        if (props.student) {
            setStudentName(props.student.firstname)
            setStudentSurname(props.student.lastname)
            setStudentId(props.student.child_id)
            setSchoolClassId(props.student.schoolclass || '')
            setBusToSchool(props.student.bus_to_school || false)
            setBusFromSchool(props.student.bus_from_school || false)
            if (props.student.date_of_birth) {
                const parts = props.student.date_of_birth.split('-')
                if (parts.length === 3) {
                    setDobYear(parts[0])
                    setDobMonth(String(parseInt(parts[1], 10)))
                    setDobDay(String(parseInt(parts[2], 10)))
                }
            }
            setComments(props.student.comments || '')
            if (props.student.parents) {
                setParents(props.student.parents)
            }
            if (props.student.schoolclass) {
                getDepartments(props.student.schoolclass, props.student.department || '')
            }
        }
    }, [])

    const validate = () => {
        const newErrors = {}
        if (!studentName.trim()) newErrors.studentName = t('students.first_name_required')
        if (!studentSurname.trim()) newErrors.studentSurname = t('students.surname_required')
        if (!schoolClassId) newErrors.schoolClassId = t('students.school_class_required')
        if (!departmentId) newErrors.departmentId = t('students.department_required')
        if (!isEditing && !(dobDay && dobMonth && dobYear)) newErrors.dob = t('students.dob_required')
        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const saveStudent = async () => {
        if (!validate()) return
        setSaving(true)
        setSaveError('')
        try {
            const response = await fetch(backendUrl + 'api/save-student/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + String(authTokens.access)
                },
                body: JSON.stringify({
                    id: studentId || null,
                    name: studentName,
                    surname: studentSurname,
                    schoolClassId,
                    departmentId,
                    bus_to_school: busToSchool,
                    bus_from_school: busFromSchool,
                    date_of_birth: (dobYear && dobMonth && dobDay)
                        ? `${dobYear}-${dobMonth.padStart(2, '0')}-${dobDay.padStart(2, '0')}`
                        : null,
                    comments,
                })
            })
            if (response.status === 200) {
                const data = await response.json()
                const savedId = studentId || data.child_id
                if (!isEditing && showAddressForm && newAddress && newLat && newLng) {
                    await fetch(backendUrl + 'api/save-student-poi/', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': 'Bearer ' + String(authTokens.access),
                        },
                        body: JSON.stringify({
                            id: null,
                            address: newAddress,
                            name: newComments,
                            studentId: savedId,
                            longitude: newLng,
                            latitude: newLat,
                        }),
                    })
                }
                setSaved(true)
                setTimeout(() => navigate('/students'), 1500)
            } else if (response.status === 401) {
                logoutUser()
            } else {
                const text = await response.text()
                setSaveError(text || t('students.failed_to_save'))
            }
        } catch {
            setSaveError(t('common.network_error'))
        } finally {
            setSaving(false)
        }
    }

    const getSchoolClasses = useCallback(async () => {
        const response = await fetch(backendUrl + 'api/schoolclasses/', {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + String(authTokens.access)
            }
        })
        if (response.status === 200) {
            const data = await response.json()
            setSchoolclasses(data.map(sc => ({ id: sc.schoolclass_id, name: sc.name })))
        } else if (response.status === 401) {
            logoutUser()
        }
    }, [])

    const getDepartments = useCallback(async (scId, selectDeptId = null) => {
        const url = backendUrl + 'api/departments/' + (scId ? `?schoolclass_id=${scId}` : '')
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + String(authTokens.access)
            }
        })
        if (response.status === 200) {
            const data = await response.json()
            setDepartments(data.map(d => ({ id: d.department_id, name: d.name })))
            if (selectDeptId) setDepartmentId(selectDeptId)
        } else if (response.status === 401) {
            logoutUser()
        }
    }, [])

    const handleSchoolclassChange = (event) => {
        const newValue = event.target.value
        setSchoolClassId(newValue)
        setDepartmentId('')
        setErrors(prev => ({ ...prev, schoolClassId: '', departmentId: '' }))
        if (newValue) {
            getDepartments(newValue)
        } else {
            setDepartments([])
        }
    }

    const handleMapClick = useCallback((lat, lng) => {
        setNewLat(lat)
        setNewLng(lng)
    }, [])

    const handleAddressSearchResult = useCallback(({ address, lat, lng }) => {
        setNewAddress(address)
        setNewLat(lat)
        setNewLng(lng)
    }, [])

    const cancelAddressForm = () => {
        setShowAddressForm(false)
        setNewAddress('')
        setNewLat(null)
        setNewLng(null)
        setNewComments('')
    }

    const addAddress = async () => {
        setSavingAddress(true)
        try {
            const response = await fetch(backendUrl + 'api/save-student-poi/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + String(authTokens.access),
                },
                body: JSON.stringify({
                    id: null,
                    address: newAddress,
                    name: newComments,
                    studentId,
                    longitude: newLng,
                    latitude: newLat,
                }),
            })
            if (response.status === 200) {
                cancelAddressForm()
                setAddressRefreshKey(k => k + 1)
            } else if (response.status === 401) {
                logoutUser()
            }
        } catch (e) {
            console.error(e)
        } finally {
            setSavingAddress(false)
        }
    }

    const mapCenter = profile
        ? [profile.schoollatitude, profile.schoollongitude]
        : [37.98, 23.73]

    const handleCreateParentChange = (e) => {
        const { name, value } = e.target
        if (name === 'phone_number') {
            setCreateParentForm(prev => ({ ...prev, phone_number: value.replace(/[^0-9]/g, '') }))
        } else {
            setCreateParentForm(prev => ({ ...prev, [name]: value }))
        }
        setCreateParentErrors(prev => ({ ...prev, [name]: '' }))
    }

    const handleCreateParentSave = async () => {
        const newErrors = {}
        if (!createParentForm.first_name.trim()) newErrors.first_name = t('common.first_name_required')
        if (!createParentForm.last_name.trim()) newErrors.last_name = t('common.last_name_required')
        if (!createParentForm.email.trim()) newErrors.email = t('common.email_required')
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(createParentForm.email)) newErrors.email = t('common.email_invalid')
        if (createParentForm.phone_number.trim() && !/^\d{1,14}$/.test(createParentForm.phone_number)) newErrors.phone_number = t('common.phone_number_invalid')
        setCreateParentErrors(newErrors)
        if (Object.keys(newErrors).length > 0) return

        setCreateParentSaving(true)
        setCreateParentError('')
        try {
            const response = await fetch(backendUrl + 'api/save-parent/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + String(authTokens.access),
                },
                body: JSON.stringify({ ...createParentForm, child_ids: studentId ? [studentId] : [] }),
            })
            if (response.ok) {
                const data = await response.json()
                setParents(prev => [...prev, {
                    profile_id: data.profile_id,
                    firstname: createParentForm.first_name,
                    lastname: createParentForm.last_name,
                    email: createParentForm.email,
                    phone_number: createParentForm.phone_number,
                }])
                setCreateParentForm({ first_name: '', last_name: '', email: '', phone_number: '', notes: '' })
                setCreateParentErrors({})
                setCreateParentModalOpen(false)
            } else {
                let msg = t('parents.failed_to_save')
                try {
                    const json = await response.json()
                    msg = json.error ? (t('parents.' + json.error, { defaultValue: '' }) || json.error) : msg
                } catch {
                    const text = await response.text()
                    msg = text || msg
                }
                setCreateParentError(msg)
            }
        } catch {
            setCreateParentError(t('common.network_error'))
        } finally {
            setCreateParentSaving(false)
        }
    }

    const closeCreateParentModal = () => {
        setCreateParentModalOpen(false)
        setCreateParentForm({ first_name: '', last_name: '', email: '', phone_number: '', notes: '' })
        setCreateParentErrors({})
        setCreateParentError('')
    }

    return (
        <Paper elevation={2} sx={{ p: { xs: 2, sm: 4 }, maxWidth: 800, mx: 'auto' }}>
            <Typography variant="h5" fontWeight={700} gutterBottom>
                {isEditing ? t('students.edit_title') : t('students.add_title')}
            </Typography>

            <Snackbar open={saved} autoHideDuration={20000} onClose={() => setSaved(false)}>
                <MuiAlert variant="filled" severity="success" sx={{ width: '100%' }}>
                    {isEditing ? t('students.updated') : t('students.created')}
                </MuiAlert>
            </Snackbar>

            {saveError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSaveError('')}>
                    {saveError}
                </Alert>
            )}

            {/* Personal Information */}
            <SectionHeader icon={<PersonIcon color="primary" />} title={t('students.personal_info')} />
            <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                    <TextField
                        label={t('students.first_name')}
                        value={studentName}
                        onChange={e => {
                            setStudentName(e.target.value)
                            setErrors(prev => ({ ...prev, studentName: '' }))
                        }}
                        error={Boolean(errors.studentName)}
                        helperText={errors.studentName}
                        fullWidth
                        required
                        variant="outlined"
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        label={t('students.surname')}
                        value={studentSurname}
                        onChange={e => {
                            setStudentSurname(e.target.value)
                            setErrors(prev => ({ ...prev, studentSurname: '' }))
                        }}
                        error={Boolean(errors.studentSurname)}
                        helperText={errors.studentSurname}
                        fullWidth
                        required
                        variant="outlined"
                    />
                </Grid>
            </Grid>

            {/* Date of Birth */}
            <Grid container spacing={2} sx={{ mt: 0 }}>
                <Grid item xs={12}>
                    <Typography variant="body2" color={errors.dob ? 'error' : 'text.secondary'} sx={{ mb: 1 }}>
                        {t('students.date_of_birth')}{!isEditing && ' *'}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <FormControl sx={{ width: 90 }} error={Boolean(errors.dob)}>
                            <InputLabel>{t('students.dob_day')}</InputLabel>
                            <Select
                                label={t('students.dob_day')}
                                value={dobDay}
                                onChange={e => setDobDay(e.target.value)}
                            >
                                <MenuItem value=""><em>—</em></MenuItem>
                                {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                                    <MenuItem key={d} value={String(d)}>{d}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl sx={{ width: 140 }} error={Boolean(errors.dob)}>
                            <InputLabel>{t('students.dob_month')}</InputLabel>
                            <Select
                                label={t('students.dob_month')}
                                value={dobMonth}
                                onChange={e => setDobMonth(e.target.value)}
                            >
                                <MenuItem value=""><em>—</em></MenuItem>
                                {[
                                    t('students.month_jan'), t('students.month_feb'), t('students.month_mar'),
                                    t('students.month_apr'), t('students.month_may'), t('students.month_jun'),
                                    t('students.month_jul'), t('students.month_aug'), t('students.month_sep'),
                                    t('students.month_oct'), t('students.month_nov'), t('students.month_dec'),
                                ].map((name, idx) => (
                                    <MenuItem key={idx + 1} value={String(idx + 1)}>{name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl sx={{ width: 110 }} error={Boolean(errors.dob)}>
                            <InputLabel>{t('students.dob_year')}</InputLabel>
                            <Select
                                label={t('students.dob_year')}
                                value={dobYear}
                                onChange={e => setDobYear(e.target.value)}
                            >
                                <MenuItem value=""><em>—</em></MenuItem>
                                {Array.from({ length: 18 }, (_, i) => new Date().getFullYear() - i).map(y => (
                                    <MenuItem key={y} value={String(y)}>{y}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                    {errors.dob && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                            {errors.dob}
                        </Typography>
                    )}
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        label={t('students.comments')}
                        value={comments}
                        onChange={e => setComments(e.target.value)}
                        fullWidth
                        multiline
                        minRows={2}
                        variant="outlined"
                        placeholder={t('students.comments_placeholder')}
                    />
                </Grid>
            </Grid>

            {/* School Information */}
            <SectionHeader icon={<SchoolIcon color="primary" />} title={t('students.school_info')} />
            <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth error={Boolean(errors.schoolClassId)} required>
                        <InputLabel>{t('students.school_class')}</InputLabel>
                        <Select
                            label={t('students.school_class')}
                            value={schoolClassId}
                            onChange={handleSchoolclassChange}
                        >
                            {schoolClasses.map(sc => (
                                <MenuItem key={sc.id} value={sc.id}>{sc.name}</MenuItem>
                            ))}
                        </Select>
                        {errors.schoolClassId && (
                            <Typography variant="caption" color="error" sx={{ ml: 1.5, mt: 0.5 }}>
                                {errors.schoolClassId}
                            </Typography>
                        )}
                    </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth error={Boolean(errors.departmentId)} disabled={!schoolClassId} required>
                        <InputLabel>{t('students.department')}</InputLabel>
                        <Select
                            label={t('students.department')}
                            value={departmentId}
                            onChange={e => {
                                setDepartmentId(e.target.value)
                                setErrors(prev => ({ ...prev, departmentId: '' }))
                            }}
                        >
                            {departments.map(d => (
                                <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
                            ))}
                        </Select>
                        {errors.departmentId && (
                            <Typography variant="caption" color="error" sx={{ ml: 1.5, mt: 0.5 }}>
                                {errors.departmentId}
                            </Typography>
                        )}
                    </FormControl>
                </Grid>
            </Grid>

            {/* Transportation */}
            <SectionHeader icon={<DirectionsBusIcon color="primary" />} title={t('students.transportation')} />
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={busToSchool}
                            onChange={e => setBusToSchool(e.target.checked)}
                        />
                    }
                    label={t('students.bus_to')}
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={busFromSchool}
                            onChange={e => setBusFromSchool(e.target.checked)}
                        />
                    }
                    label={t('students.bus_from')}
                />
            </Box>

            {/* Parents */}
            {isEditing && (
                <>
                    <SectionHeader icon={<PeopleIcon color="primary" />} title={t('students.parents_guardians')} />
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                        {parents.map(p => (
                            <Chip
                                key={p.profile_id}
                                label={`${p.firstname} ${p.lastname}`}
                                variant="outlined"
                                onClick={() => { setSelectedParent(p); setParentModalOpen(true); }}
                                sx={{ cursor: 'pointer' }}
                            />
                        ))}
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={() => setCreateParentModalOpen(true)}
                        >
                            {elUpperCase(t('students.add_new_parent'))}
                        </Button>
                    </Box>
                </>
            )}

            {/* Parent details modal */}
            <Dialog open={parentModalOpen} onClose={() => setParentModalOpen(false)} maxWidth="xs" fullWidth>
                {selectedParent && (
                    <>
                        <DialogTitle>
                            {selectedParent.firstname} {selectedParent.lastname}
                        </DialogTitle>
                        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {selectedParent.email && (
                                <Typography variant="body2" color="text.secondary">
                                    {t('common.email')}: <strong>{selectedParent.email}</strong>
                                </Typography>
                            )}
                            {selectedParent.phone_number && (
                                <Typography variant="body2" color="text.secondary">
                                    {t('common.phone_number')}: <strong>{selectedParent.phone_number}</strong>
                                </Typography>
                            )}
                            <MuiLink
                                href={`/add-parent/${selectedParent.profile_id}`}
                                underline="hover"
                                sx={{ fontSize: '0.9rem', mt: 1 }}
                            >
                                {t('students.go_to_edit_parent')}
                            </MuiLink>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setParentModalOpen(false)}>{t('common.close')}</Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>

            {/* Create new parent modal */}
            <Dialog open={createParentModalOpen} onClose={closeCreateParentModal} maxWidth="sm" fullWidth>
                <DialogTitle>{t('students.create_parent_title')}</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
                    {createParentError && (
                        <Alert severity="error" onClose={() => setCreateParentError('')}>{createParentError}</Alert>
                    )}
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                            label={t('parents.first_name')}
                            name="first_name"
                            value={createParentForm.first_name}
                            onChange={handleCreateParentChange}
                            error={Boolean(createParentErrors.first_name)}
                            helperText={createParentErrors.first_name}
                            fullWidth
                            size="small"
                            required
                        />
                        <TextField
                            label={t('parents.last_name')}
                            name="last_name"
                            value={createParentForm.last_name}
                            onChange={handleCreateParentChange}
                            error={Boolean(createParentErrors.last_name)}
                            helperText={createParentErrors.last_name}
                            fullWidth
                            size="small"
                            required
                        />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                            label={t('parents.email')}
                            name="email"
                            type="email"
                            value={createParentForm.email}
                            onChange={handleCreateParentChange}
                            error={Boolean(createParentErrors.email)}
                            helperText={createParentErrors.email}
                            fullWidth
                            size="small"
                            required
                        />
                        <TextField
                            label={t('parents.phone')}
                            name="phone_number"
                            value={createParentForm.phone_number}
                            onChange={handleCreateParentChange}
                            error={Boolean(createParentErrors.phone_number)}
                            helperText={createParentErrors.phone_number}
                            fullWidth
                            size="small"
                        />
                    </Box>
                    <TextField
                        label={t('parents.notes')}
                        name="notes"
                        value={createParentForm.notes}
                        onChange={handleCreateParentChange}
                        fullWidth
                        size="small"
                        multiline
                        minRows={2}
                        placeholder={t('parents.notes_placeholder')}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={closeCreateParentModal} disabled={createParentSaving}>
                        {t('parents.cancel')}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleCreateParentSave}
                        disabled={createParentSaving}
                        startIcon={createParentSaving ? <CircularProgress size={16} color="inherit" /> : null}
                    >
                        {createParentSaving ? t('parents.saving') : t('parents.save')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Addresses */}
            <>
                <SectionHeader icon={<HomeIcon color="primary" />} title={t('students.addresses')} />
                {studentId && <StudentAddressesComponent key={addressRefreshKey} studentId={studentId} />}
                <Box sx={{ mt: 1 }}>
                    {!showAddressForm ? (
                        <>
                            {!isEditing && newAddress && newLat && (
                                <Alert
                                    severity="info"
                                    sx={{ mb: 1 }}
                                    onClose={cancelAddressForm}
                                >
                                    <Typography variant="body2">{newAddress}</Typography>
                                    {newComments && <Typography variant="caption">{newComments}</Typography>}
                                </Alert>
                            )}
                            {(isEditing || !newAddress || !newLat) && (
                                <Button
                                    variant="outlined"
                                    startIcon={<AddIcon />}
                                    size="small"
                                    onClick={() => setShowAddressForm(true)}
                                >
                                    {t('students.add_address')}
                                </Button>
                            )}
                        </>
                    ) : (
                        <Box sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                                {t('students.add_address')}
                            </Typography>
                            <GoogleAddressSearch
                                value={newAddress}
                                onChange={val => setNewAddress(val)}
                                onSelect={handleAddressSearchResult}
                                label={t('students.search_address')}
                            />
                            <Box sx={{ mt: 1, borderRadius: '8px', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                                <MapContainer
                                    center={mapCenter}
                                    zoom={14}
                                    style={{ height: 300, width: '100%', cursor: 'crosshair' }}
                                >
                                    <TileLayer
                                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                    />
                                    <MapClickHandler onMapClick={handleMapClick} />
                                    <FlyTo position={newLat && newLng ? [newLat, newLng] : null} />
                                    {newLat && newLng && <Marker position={[newLat, newLng]} />}
                                </MapContainer>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                                <FormControl sx={{ flex: 1, minWidth: 200 }}>
                                    <InputLabel htmlFor="new-comments">{t('students.address_comments')}</InputLabel>
                                    <Input
                                        id="new-comments"
                                        value={newComments}
                                        onChange={e => setNewComments(e.target.value)}
                                    />
                                    <FormHelperText> </FormHelperText>
                                </FormControl>
                                <Box sx={{ display: 'flex', gap: 1, pb: '20px' }}>
                                    {isEditing ? (
                                        <Button
                                            variant="contained"
                                            size="small"
                                            onClick={addAddress}
                                            disabled={!newLat || !newAddress || savingAddress}
                                            startIcon={savingAddress ? <CircularProgress size={14} color="inherit" /> : null}
                                        >
                                            {t('common.save')}
                                        </Button>
                                    ) : (
                                        <Button
                                            variant="contained"
                                            size="small"
                                            onClick={() => setShowAddressForm(false)}
                                            disabled={!newLat || !newAddress}
                                        >
                                            {t('common.ok')}
                                        </Button>
                                    )}
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={cancelAddressForm}
                                        disabled={savingAddress}
                                    >
                                        {t('common.cancel')}
                                    </Button>
                                </Box>
                            </Box>
                        </Box>
                    )}
                </Box>
            </>

            {/* Actions */}
            <Divider sx={{ mt: 4, mb: 2 }} />
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                    onClick={() => navigate('/students', { replace: true })}
                    variant="outlined"
                    color="inherit"
                    disabled={saving}
                >
                    {t('common.cancel')}
                </Button>
                <Button
                    onClick={saveStudent}
                    variant="contained"
                    disabled={saving}
                    startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
                >
                    {saving ? t('common.saving') : isEditing ? t('students.save_changes') : t('students.add')}
                </Button>
            </Box>
        </Paper>
    )
}

export default StudentDetailsComponent
